import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import Landing from "@/pages/Landing";
import B2BLanding from "@/pages/B2BLanding";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import BecomeDriver from "@/pages/BecomeDriver";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicDriverPage from "@/pages/PublicDriverPage";

import CustomerHome from "@/pages/customer/Home";
import CustomerNewOrder from "@/pages/customer/NewOrder";
import CustomerOrders from "@/pages/customer/Orders";
import CustomerOrderDetail from "@/pages/customer/OrderDetail";
import LabelInstructionsPage from "@/pages/customer/LabelInstructions";
import CustomerProfile from "@/pages/customer/Profile";
import CustomerSubscription from "@/pages/customer/Subscription";
import CustomerInvoices from "@/pages/customer/Invoices";

import DriverHome from "@/pages/driver/Home";
import DriverActive from "@/pages/driver/Active";
import DriverEarnings from "@/pages/driver/Earnings";
import DriverOrderDetail from "@/pages/driver/OrderDetail";
import DriverProfile from "@/pages/driver/Profile";
import DriverReferral from "@/pages/driver/Referral";
import DriverDeliveryHistory from "@/pages/driver/DeliveryHistory";
import DriverVehicle from "@/pages/driver/Vehicle";
import DriverDocuments from "@/pages/driver/Documents";
import DriverPayout from "@/pages/driver/Payout";
import DriverRoutes from "@/pages/driver/Routes";
import DriverRouteSearch from "@/pages/driver/RouteSearch";
import DriverTaxSummary from "@/pages/driver/TaxSummary";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminOrders from "@/pages/admin/Orders";
import AdminUsers from "@/pages/admin/Users";
import AdminCustomerDetail from "@/pages/admin/CustomerDetail";
import AdminDrivers from "@/pages/admin/Drivers";
import AdminDriverDetail from "@/pages/admin/DriverDetail";
import AdminSettings from "@/pages/admin/Settings";
import AdminLeads from "@/pages/admin/Leads";
import AdminNotificationLogs from "@/pages/admin/NotificationLogs";
import AdminAcquisition from "@/pages/admin/Acquisition";
import AdminPayouts from "@/pages/admin/Payouts";
import AdminRefunds from "@/pages/admin/Refunds";
import AdminOps from "@/pages/admin/Ops";
import AdminCrossBorderReport from "@/pages/admin/CrossBorderReport";
import AdminRetryQueue from "@/pages/admin/RetryQueue";
import AdminAuditLog from "@/pages/admin/AuditLog";
import AdminDriverPilot from "@/pages/admin/DriverPilot";
import LegalInformation from "@/pages/LegalInformation";
import PublicTrackingPage from "@/pages/PublicTrackingPage";

const CUSTOMER_ROLES = ["customer_private", "customer_business", "customer_shop"];

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  return <Navigate to="/app" replace />;
}

function App() {
  useEffect(() => {
    document.title = "DapNow — Consegne in giornata";
  }, []);

  // Phase 44.15 + 44.16 + 44.21 + 44.22 — Fix schermata bianca al ritorno da
  // app esterna (Google Maps post-ritiro).
  //
  // Phase 44.22 — Threshold AGGRESSIVO quando il driver è in fase di consegna.
  // Il flag `body[data-aggressive-reload="true"]` viene settato dalla pagina
  // driver/OrderDetail.jsx quando `order.status ∈ {picked_up, delivering}`.
  // In quella fase l'utente apre Google Maps SEMPRE intenzionalmente per
  // raggiungere il destinatario (mai swipe spuri tra tab) → reload anche per
  // ritorni brevissimi (1s) elimina TUTTI i casi di schermata bianca post-pickup.
  // In tutte le altre fasi il threshold resta 8s (bilanciato).
  useEffect(() => {
    let hiddenAt = null;
    const DEFAULT_THRESHOLD_MS = 8_000;
    const AGGRESSIVE_THRESHOLD_MS = 1_000;

    const doReload = () => { try { window.location.reload(); } catch { /* silent */ } };
    const currentThreshold = () =>
      document.body.getAttribute("data-aggressive-reload") === "true"
        ? AGGRESSIVE_THRESHOLD_MS
        : DEFAULT_THRESHOLD_MS;

    const onPageShow = (e) => {
      if (e.persisted) { doReload(); return; }
      if (hiddenAt) {
        const elapsed = Date.now() - hiddenAt;
        hiddenAt = null;
        if (elapsed > currentThreshold()) doReload();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else if (document.visibilityState === "visible" && hiddenAt) {
        const elapsed = Date.now() - hiddenAt;
        hiddenAt = null;
        if (elapsed > currentThreshold()) doReload();
      }
    };

    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            richColors
            position="top-center"
            expand
            visibleToasts={5}
            gap={12}
            offset={16}
            toastOptions={{ className: "shadow-lg" }}
            style={{ zIndex: 50 }}
          />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/legal" element={<LegalInformation />} />
            <Route path="/business" element={<B2BLanding />} />
            <Route path="/track/:token" element={<PublicTrackingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/become-driver" element={<BecomeDriver />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/d/:token" element={<PublicDriverPage />} />

            {/* Customer */}
            <Route
              path="/app"
              element={
                <ProtectedRoute roles={CUSTOMER_ROLES}>
                  <AppShell><CustomerHome /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/new"
              element={
                <ProtectedRoute roles={CUSTOMER_ROLES}>
                  <AppShell><CustomerNewOrder /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/orders"
              element={
                <ProtectedRoute roles={CUSTOMER_ROLES}>
                  <AppShell><CustomerOrders /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/orders/:id"
              element={
                <ProtectedRoute roles={CUSTOMER_ROLES}>
                  <AppShell><CustomerOrderDetail /></AppShell>
                </ProtectedRoute>
              }
            />
            {/* Phase 44.28 — Schermata dedicata istruzioni etichetta post-pagamento Stripe */}
            <Route
              path="/app/orders/:id/label"
              element={
                <ProtectedRoute roles={CUSTOMER_ROLES}>
                  <AppShell><LabelInstructionsPage /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/profile"
              element={
                <ProtectedRoute roles={CUSTOMER_ROLES}>
                  <AppShell><CustomerProfile /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/subscription"
              element={
                <ProtectedRoute roles={CUSTOMER_ROLES}>
                  <AppShell><CustomerSubscription /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app/invoices"
              element={
                <ProtectedRoute roles={CUSTOMER_ROLES}>
                  <AppShell><CustomerInvoices /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Driver */}
            <Route
              path="/driver"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverHome /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/active"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverActive /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/earnings"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverEarnings /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/orders/:id"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverOrderDetail /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/profile"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverProfile /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/referral"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverReferral /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/history"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverDeliveryHistory /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/vehicle"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverVehicle /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/documents"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverDocuments /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/payout"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverPayout /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/routes"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverRoutes /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/route-search"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverRouteSearch /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/tax-summary"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <AppShell><DriverTaxSummary /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/driver/stripe-connect"
              element={
                <ProtectedRoute roles={["driver"]}>
                  <Navigate to="/driver/profile" replace />
                </ProtectedRoute>
              }
            />

            {/* Admin */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminOrders /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminUsers /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/customers/:id"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminCustomerDetail /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/drivers"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminDrivers /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/drivers/:id"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminDriverDetail /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/driver-pilot"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminDriverPilot /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/leads"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminLeads /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminNotificationLogs /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/acquisition"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminAcquisition /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payouts"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminPayouts /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/refunds"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminRefunds /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/ops"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminOps /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cross-border"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminCrossBorderReport /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/retry-queue"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminRetryQueue /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-log"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminAuditLog /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <AppShell><AdminSettings /></AppShell>
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
